import { ShieldCheck, CreditCard, Search, BellRing } from "lucide-react";

const SMARTPAY_URL =
  "https://cic.cra.xedalink.net/smartpay/SmartPay.aspx?uid=fcf7fec3-0bc8-44c5-9f90-617b029dc9ab";
const ITIN_CREDIT_URL = "https://myitincredit.com/";

export function CreditSection() {
  return (
    <section
      aria-labelledby="credit-section-heading"
      className="border-t border-border bg-background py-16"
    >
      <div className="container mx-auto max-w-7xl px-4">
        <div className="mx-auto max-w-3xl text-center">
          <span className="text-xs font-semibold uppercase tracking-widest text-brand-gold">
            Credit & pre-approval
          </span>
          <h2
            id="credit-section-heading"
            className="mt-3 font-display text-3xl font-bold text-foreground md:text-4xl"
          >
            Why we pull credit for your investor loan pre-approval
          </h2>
          <p className="mt-4 text-base text-muted-foreground">
            Every DSCR / business-purpose loan file needs a tri-merge credit report so we can
            verify identity, price your rate tier accurately, and confirm reserves and payment
            history for the underwriter. Cleaner credit typically unlocks a better rate and
            higher leverage.
          </p>
        </div>

        <div className="mt-10 grid gap-6 md:grid-cols-2">
          <div className="flex flex-col rounded-2xl border border-border bg-card p-6 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="grid h-11 w-11 place-items-center rounded-xl bg-brand/10 text-brand">
                <CreditCard className="h-5 w-5" aria-hidden />
              </div>
              <h3 className="font-display text-xl text-foreground">
                Pay for your credit report — SmartPay
              </h3>
            </div>
            <p className="mt-4 text-sm text-muted-foreground">
              Simply Approved Mortgages uses MeridianLink SmartPay to securely collect the
              credit report fee for your investor pre-approval. Payment goes directly to the
              credit vendor — not to us — and unlocks your tri-merge report (Equifax, Experian,
              TransUnion) so your loan officer can price your file.
            </p>
            <ul className="mt-4 space-y-2 text-sm text-foreground/90">
              <li className="flex gap-2">
                <ShieldCheck className="mt-0.5 h-4 w-4 flex-none text-brand" aria-hidden />
                Secure, PCI-compliant checkout hosted by MeridianLink
              </li>
              <li className="flex gap-2">
                <ShieldCheck className="mt-0.5 h-4 w-4 flex-none text-brand" aria-hidden />
                Required for a formal investor pre-approval decision
              </li>
              <li className="flex gap-2">
                <ShieldCheck className="mt-0.5 h-4 w-4 flex-none text-brand" aria-hidden />
                Soft-touch process — your loan officer will guide you through it
              </li>
            </ul>
            <a
              href={SMARTPAY_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-6 inline-flex items-center justify-center rounded-xl bg-brand px-5 py-3 font-medium text-white transition hover:opacity-90"
            >
              Pay for credit report securely
            </a>
            <p className="mt-3 text-xs text-muted-foreground">
              You'll be redirected to cic.cra.xedalink.net (SmartPay).
            </p>
          </div>

          <div className="flex flex-col rounded-2xl border border-border bg-card p-6 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="grid h-11 w-11 place-items-center rounded-xl bg-brand-gold/20 text-brand-gold">
                <Search className="h-5 w-5" aria-hidden />
              </div>
              <h3 className="font-display text-xl text-foreground">
                Check your credit first — $1 trial at MyITINCredit
              </h3>
            </div>
            <p className="mt-4 text-sm text-muted-foreground">
              Before you apply, it's smart to know exactly where your credit stands.
              MyITINCredit offers a <strong>$1 trial for 15 days</strong> that includes all
              three credit reports and scores (Equifax, Experian, TransUnion), plus ongoing
              credit monitoring so you can catch errors, dispute inaccuracies, and watch for
              identity theft.
            </p>
            <ul className="mt-4 space-y-2 text-sm text-foreground/90">
              <li className="flex gap-2">
                <Search className="mt-0.5 h-4 w-4 flex-none text-brand-gold" aria-hidden />
                See all 3 bureau reports & scores before your lender does
              </li>
              <li className="flex gap-2">
                <BellRing className="mt-0.5 h-4 w-4 flex-none text-brand-gold" aria-hidden />
                Ongoing monitoring alerts you to new accounts or score changes
              </li>
              <li className="flex gap-2">
                <ShieldCheck className="mt-0.5 h-4 w-4 flex-none text-brand-gold" aria-hidden />
                Fix errors early — cleaner credit can widen your investor loan options
              </li>
            </ul>
            <a
              href={ITIN_CREDIT_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-6 inline-flex items-center justify-center rounded-xl border border-brand-gold bg-brand-gold/10 px-5 py-3 font-medium text-brand-gold transition hover:bg-brand-gold/20"
            >
              Start $1 / 15-day trial
            </a>
            <p className="mt-3 text-xs text-muted-foreground">
              You'll be redirected to myitincredit.com. Third-party service — terms apply.
            </p>
          </div>
        </div>

        <p className="mx-auto mt-8 max-w-3xl text-center text-xs text-muted-foreground">
          Credit report fees are paid directly to the credit vendor. Simply Approved Mortgages
          does not profit from the credit pull. MyITINCredit is an independent third-party
          service; pricing, terms, and features are set by that provider.
        </p>
      </div>
    </section>
  );
}
